// frontend/src/contexts/InstitutionConfigContext.tsx
/**
 * InstitutionConfigContext — per-institution config the frontend reads at app
 * init (today: just `hasGradeData`; workstream E will add GE-area metadata
 * to the same payload).
 *
 * Resolution order at mount:
 *   1. localStorage cache (fast, may be slightly stale within TTL window)
 *   2. Network fetch from GET /api/institution/current
 *   3. FALLBACK_CONFIG constant (defensive — preserves existing UMD behavior
 *      if the network is unreachable AND no cache exists)
 *
 * Workstream H/2 of multi-institution prep. The provider is mounted once
 * inside AuthProvider in App.tsx so the auth-gated fetch has a token.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { apiClient } from '../utils/apiClient';
import { makeInstitutionLocalStorage } from '../utils/institutionLocalStorage';
import { UMD_GEN_ED_AREAS, type GenEdArea } from '../fallbacks/umdGenEdAreas';
import { UMD_GRADE_MARKS } from '../fallbacks/umdGradeMarks';
import { resolveTheme, type InstitutionTheme } from '../themes';
import { useActiveInstitution } from './ActiveInstitutionContext';
import { useAuth } from './AuthContext';
import { DEFAULT_WORKLOAD_THRESHOLDS } from '../utils/courseUtils';

// =============================================================================
// CONSTANTS
// =============================================================================

// Bumped v1 → v2 → v3.
//   v2: payload added `slug` (Phase 4).
//   v3: cache key suffixed with the institution slug so two institutions
//       don't share a cached payload when a user crosses subdomains
//       (Phase F). Old v2 entries removed by `migrateLegacyCacheKeys()`.
//   Phase E3 (2026-05-16): slug derivation shifted from host-only to
//       active-membership-first (host fallback for pre-auth). The slug
//       value semantics are unchanged for single-membership users, so
//       no key-prefix bump is needed — existing v3 entries remain valid.
const CACHE_KEY_PREFIX = 'academic_planner_institution_config_v3';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

// Default slug when nothing is known — keeps UMD as the safe baseline.
const DEFAULT_SLUG = 'umd';

// M2 (Workstream M, 2026-05-19): cache get/set/clear + TTL + legacy-key
// migration extracted to `utils/institutionLocalStorage`. Same pattern as
// ActiveInstitutionContext. Constructor fires the legacy-key cleanup once
// on module import (idempotent on re-imports).
//
// Cache key version history:
//   v1: unscoped (pre-Phase F)
//   v2: payload added `slug` (Phase 4); still unscoped
//   v3: cache key suffixed with the institution slug so two institutions
//       don't share a cached payload when a user crosses subdomains
//       (Phase F).
//   Phase E3 (2026-05-16): slug derivation shifted from host-only to
//       active-membership-first; v3 semantics unchanged for single-
//       membership users, so no key-prefix bump.
const configCache = makeInstitutionLocalStorage<InstitutionConfig>(CACHE_KEY_PREFIX, {
  ttlMs: CACHE_TTL_MS,
  legacyKeysToRemove: [
    'academic_planner_institution_config_v2',
    'academic_planner_institution_config', // v1
  ],
});

// Defensive fallback if both cache and network fail. Matches UMD's current
// behavior so the UI keeps working in offline / API-unreachable scenarios.
// After the institution-as-data refactor, a per-institution fallback file
// lives in src/fallbacks/<slug>.ts and the choice happens at boot.
/** The word for a unit of credit at the active institution
 *  (`planning.credit_unit`). US schools: credit/credits/cr; UNIC: ECTS for
 *  all three. Components render every credit label through
 *  `useCreditUnit()` — never the literal word "credits" (a UNIC semester
 *  read "28 credits" beside a chat reply that said ECTS, 2026-10-21). */
export interface CreditUnit {
  singular: string;
  plural: string;
  abbrev: string;
}

export const DEFAULT_CREDIT_UNIT: CreditUnit = { singular: 'credit', plural: 'credits', abbrev: 'cr' };

/** The wire shape of `credit_unit` on /api/institution/current: partial or absent on older backends. */
type RawCreditUnit = { singular?: string; plural?: string; abbrev?: string } | null | undefined;

const FALLBACK_CONFIG: InstitutionConfig = {
  slug: DEFAULT_SLUG,
  hasGradeData: true,
  genEdAreas: UMD_GEN_ED_AREAS,
  // UMD has AP credit policy loaded (workstream N1+); safe default.
  supportsApCredits: true,
  // UMD's PLC cap; matches the backend config-service default for
  // unseeded institutions.
  priorLearningCap: 60,
  gradeMarks: [...UMD_GRADE_MARKS],
  // UMD's graduation target; matches the backend config-service default.
  graduationCredits: 120,
  // UMD's normal load and per-semester maximum (the values the workload
  // warning hardcoded before it read config).
  workloadThresholds: DEFAULT_WORKLOAD_THRESHOLDS,
  // No hardcoded list: the pickers offer only "Any department" until the
  // fetch lands, rather than showing one institution's departments to all.
  departments: [],
  creditUnit: DEFAULT_CREDIT_UNIT,
  // Every flag off until the fetch says otherwise.
  features: {},
};

// =============================================================================
// TYPES
// =============================================================================

/** One department at the active institution (`departments` table). Some
 *  institutions seed `name` equal to `code` (SJSU). */
export interface InstitutionDepartment {
  code: string;
  name: string;
}

/** Picker label: "Mathematics (MATH)", or just the code when the name adds
 *  nothing (SJSU seeds name = code). */
export const formatDepartmentLabel = (dept: InstitutionDepartment): string =>
  dept.name && dept.name !== dept.code ? `${dept.name} (${dept.code})` : dept.code;

export interface WorkloadThresholds {
  moderateAbove: number;
  heavyAbove: number;
}

export interface InstitutionConfig {
  /** Lowercase URL slug for this institution (`umd`, `sjsu`, …). Used to
   *  pick a theme from the registry in `src/themes/`. */
  slug: string;
  /** True if this institution has a grade-history data source (PlanetTerp
   *  for UMD). Frontend hides grade charts / difficulty badges / GPA-based
   *  sort options when false. */
  hasGradeData: boolean;
  /** Per-institution GE areas. Drives GE-area pickers and the dual-progress
   *  bar layout. EMPTY means the institution has no general education
   *  (e.g. UNIC); consumers hide GE controls rather than render an empty
   *  picker. Only FALLBACK_CONFIG (config unreachable) carries UMD's list. */
  genEdAreas: GenEdArea[];
  /** True if this institution has AP credit policy data loaded
   *  (`ap_exams` rows exist scoped to it). Frontend uses this to gate
   *  the "Manage AP Credits" modal trigger — hidden for institutions
   *  whose AP YAML hasn't been authored + loaded yet. Workstream N7. */
  supportsApCredits: boolean;
  /** Max total prior-learning credits (AP/IB/CLEP) a student may apply
   *  toward their degree at this institution (UMD 60; SJSU 30 per CSU
   *  EO 1100). Drives PLC cap indicators + over-cap warnings — never
   *  hardcode 60 in components. UX-R1 task 1.1. `null` = the institution
   *  publishes NO total PLC cap (UC Merced, W41 M5). */
  priorLearningCap: number | null;
  /** Every mark a student may record at this institution, ladder grades
   *  first — from `institution_config.grade_scale` (W43 P3). */
  gradeMarks: string[];
  /** Credits required to graduate (`planning.graduation_credits`) — 120 at
   *  the four-year institutions, 60/90 at semester/quarter community
   *  colleges. Drives every credit denominator; never render the 120
   *  literal in components. W49 A3. */
  graduationCredits: number;
  /** Semester loads the workload warning compares against, in the
   *  institution's own unit: moderate above its normal load, heavy above its
   *  per-semester maximum. UMD 16/18; UNIC 30/36 ECTS. Never hardcode a US
   *  credit scale in components. */
  workloadThresholds: WorkloadThresholds;
  /** Departments with at least one active course, ordered by code. Drives
   *  every department picker; never hardcode one institution's list. */
  departments: InstitutionDepartment[];
  /** The institution's word for a unit of credit — see `CreditUnit`. */
  creditUnit: CreditUnit;
  /** Club feature flags (`institution_config.features`, {slug: bool}). A
   *  missing slug is OFF. Read through `isFeatureEnabled(slug)` on the
   *  context value. Display only: the gated routes enforce the flag
   *  server-side (404 while off), so the shell uses this to decide what to
   *  offer, never what is allowed. */
  features: Record<string, boolean>;
}

export interface InstitutionConfigContextValue {
  config: InstitutionConfig;
  /** True while a signed-in user's config for the active school has not
   *  arrived yet: from sign-in (memberships included, since they name the
   *  school), and again after a school switch, until the fetch settles. A
   *  failed fetch settles too, leaving that school's cache or the fallback,
   *  never another school's config.
   *  `config` holds the cached/fallback value meanwhile, so most consumers
   *  can ignore this. A consumer that acts on a value (e.g. redirects on a
   *  feature flag) must wait for it to go false, or it acts on the fallback,
   *  where every flag is off. Always false pre-auth (nothing to fetch). */
  loading: boolean;
  /** True only when `config.features[slug] === true`. Unknown slugs, a
   *  pre-fetch state, and FALLBACK_CONFIG all read false. */
  isFeatureEnabled: (slug: string) => boolean;
}

/** Shared implementation so the default context value and the provider
 *  agree: strict `=== true`, never truthiness. */
const featureEnabled = (features: Record<string, boolean> | undefined, slug: string): boolean =>
  features?.[slug] === true;

// =============================================================================
// CONTEXT
// =============================================================================

// Default value (not null) so components rendered outside the provider
// — primarily in unit tests — get a sensible config rather than a throw.
// Production wiring is in App.tsx; if that's removed, components silently
// render with FALLBACK_CONFIG (hasGradeData=true) which preserves UMD's
// current behavior exactly. This is a deliberate trade-off vs the strict
// "throw on missing provider" pattern used by AppContext: institution
// config is non-critical UI gating, and the defensive fallback is safer
// for ergonomics than catching wiring bugs at runtime.
export const InstitutionConfigContext =
  createContext<InstitutionConfigContextValue>({
    config: FALLBACK_CONFIG,
    loading: false,
    isFeatureEnabled: (slug) => featureEnabled(FALLBACK_CONFIG.features, slug),
  });

export const useInstitutionConfig = (): InstitutionConfigContextValue =>
  useContext(InstitutionConfigContext);

/** The credit-unit words plus two formatters. `label(n)` picks singular or
 *  plural ("credit" / "credits"; ECTS is invariant), `format(n)` renders
 *  "6 credits" / "6 ECTS", `heading` is the capitalised plural for column
 *  and field headings ("Credits" / "ECTS"). */
export interface CreditUnitLabels extends CreditUnit {
  heading: string;
  label: (n: number | string | null | undefined) => string;
  format: (n: number | string | null | undefined) => string;
}

export const creditUnitLabels = (unit: CreditUnit): CreditUnitLabels => {
  const label = (n: number | string | null | undefined): string =>
    Number(n) === 1 ? unit.singular : unit.plural;
  const heading = unit.plural === unit.plural.toUpperCase()
    ? unit.plural
    : unit.plural.charAt(0).toUpperCase() + unit.plural.slice(1);
  return {
    ...unit,
    heading,
    label,
    format: (n) => `${n ?? 0} ${label(n)}`,
  };
};

export const useCreditUnit = (): CreditUnitLabels => {
  const { config } = useInstitutionConfig();
  const unit = config.creditUnit ?? DEFAULT_CREDIT_UNIT;
  return useMemo(() => creditUnitLabels(unit), [unit]);
};

/** Resolve the active institution's branding theme. Thin wrapper over
 *  `resolveTheme(config.slug)` so consumers don't need to import both
 *  the context and the theme registry separately. Returns the UMD
 *  default for unknown slugs (see resolveTheme's fallback behavior). */
export const useInstitutionTheme = (): InstitutionTheme => {
  const { config } = useInstitutionConfig();
  return resolveTheme(config.slug);
};

// =============================================================================
// CACHE HELPERS
// =============================================================================

const loadFromCache = (slug: string): InstitutionConfig | null => {
  const cached = configCache.load(slug);
  if (!cached) return null;
  // Defense in depth: cached slug should match the lookup slug; if not,
  // treat as miss (catches corrupted caches and post-deploy migrations).
  if (cached.slug !== slug) return null;
  // Shape guard: entries cached before UX-R1 task 1.1 lack
  // priorLearningCap. Treat them as a miss (fall back → refetch) rather
  // than bumping the cache-key version for one additive field.
  // Accept a number OR null (null = no cap, W41 M5); reject undefined
  // (pre-UX-R1 cache entries) as a miss so they refetch.
  if (cached.priorLearningCap !== null && typeof cached.priorLearningCap !== 'number') return null;
  // Same pattern for W49 A3's graduationCredits: pre-W49 entries lack it.
  if (typeof cached.graduationCredits !== 'number') return null;
  // Entries cached before `departments` existed: keep the entry (so the
  // active school's theme still paints first) with an empty list. The
  // provider refetches on every signed-in mount, which fills it in.
  const withDepartments = Array.isArray(cached.departments)
    ? cached
    : { ...cached, departments: [] };
  // Entries cached before `workloadThresholds` existed: same — keep the entry
  // and fill in the default scale. A miss would paint the UMD fallback (theme,
  // AP support) for the same window, and its thresholds are these defaults
  // anyway; the refetch brings the institution's own.
  // Entries cached before `creditUnit` existed: same — keep the entry with
  // the US default words; the refetch brings the institution's own.
  const withUnit = typeof withDepartments.creditUnit?.plural === 'string'
    ? withDepartments
    : { ...withDepartments, creditUnit: DEFAULT_CREDIT_UNIT };
  const withThresholds = typeof withUnit.workloadThresholds?.heavyAbove === 'number'
    ? withUnit
    : { ...withUnit, workloadThresholds: FALLBACK_CONFIG.workloadThresholds };
  // Entries cached before `features` existed: keep the entry with every
  // flag off — the same reading an absent key gets from the backend — and
  // let the mount-time refetch bring the real map.
  return isBooleanMap(withThresholds.features)
    ? withThresholds
    : { ...withThresholds, features: {} };
};

const isBooleanMap = (v: unknown): v is Record<string, boolean> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)
  && Object.values(v as Record<string, unknown>).every((x) => typeof x === 'boolean');

/** Only boolean values survive: a non-boolean flag reads OFF here, as the
 *  backend validator reads a malformed `features` key as all-off. */
const parseFeatures = (raw: unknown): Record<string, boolean> => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>)
      .filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
  );
};

const saveToCache = (config: InstitutionConfig): void => {
  configCache.save(config.slug, config);
};

// =============================================================================
// FETCH
// =============================================================================

/** Shape of the institution-config response AFTER apiClient.unwrapResponse.
 *
 *  The backend emits `{success: true, data: {institution: {...}}}` (see
 *  `backend/routes/institution.py`). The apiClient's `unwrapResponse`
 *  flattens this to `{success: true, institution: {...}}` — see
 *  `utils/apiClient.ts:unwrapResponse`. So the fetcher reads
 *  `body.institution`, not `body.data.institution`.
 *
 *  History: pre-Phase-4 the fetcher read `body.data.institution`, which
 *  has been silently failing in production since 22fb15ba (Phase 4.1).
 *  UMD never noticed because the catch handler falls back to UMD config,
 *  which is what UMD users see anyway. SJSU users see the same UMD
 *  fallback — visibly wrong — surfaced 2026-05-12 during C4 verification.
 */
interface InstitutionConfigApiResponse {
  success?: boolean;
  institution?: {
    slug?: string;
    has_grade_data?: boolean;
    gen_ed_areas?: GenEdArea[];
    supports_ap_credits?: boolean;
    prior_learning_cap?: number | null;
    credit_unit?: RawCreditUnit;
    graduation_credits?: number;
    workload_thresholds?: { moderate_above?: number; heavy_above?: number };
    grade_marks?: string[];
    departments?: InstitutionDepartment[];
    features?: Record<string, boolean>;
  } | null;
}

/** Positive numbers only; anything else falls back to UMD's values field by
 *  field, and a moderate threshold above the heavy one is clamped to it. */
/** A partial or missing `credit_unit` (pre-125 backend, cached entry) falls
 *  back field by field to the US default. */
const parseCreditUnit = (raw: RawCreditUnit): CreditUnit => ({
  singular: typeof raw?.singular === 'string' && raw.singular ? raw.singular : DEFAULT_CREDIT_UNIT.singular,
  plural: typeof raw?.plural === 'string' && raw.plural ? raw.plural : DEFAULT_CREDIT_UNIT.plural,
  abbrev: typeof raw?.abbrev === 'string' && raw.abbrev ? raw.abbrev : DEFAULT_CREDIT_UNIT.abbrev,
});

const parseWorkloadThresholds = (
  raw: { moderate_above?: number; heavy_above?: number } | undefined,
): WorkloadThresholds => {
  const fallback = FALLBACK_CONFIG.workloadThresholds;
  const pick = (v: unknown, d: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : d;
  const heavyAbove = pick(raw?.heavy_above, fallback.heavyAbove);
  const moderateAbove = Math.min(pick(raw?.moderate_above, fallback.moderateAbove), heavyAbove);
  return { moderateAbove, heavyAbove };
};

/** Fetch from the backend via the central apiClient (auto auth header,
 *  token refresh, error interceptors). Throws on any failure.
 *
 *  DELIBERATE raw-apiClient use (S5 F1): this is the institution-context
 *  bootstrap. typedApi is the rule everywhere else, but this fetch is what
 *  *establishes* the active-institution slug the typed client's
 *  X-Institution-Slug interceptor sends — routing it through typedApi would
 *  be the chicken-and-egg. Same for ActiveInstitutionContext's
 *  /api/institutions/me call.
 *
 *  An empty `gen_ed_areas` list from the backend is kept EMPTY: it means the
 *  institution has no general education (UNIC, the first such institution).
 *  Substituting UMD's areas here rendered UMD's GE codes at a school that has
 *  none. The UMD list remains only in FALLBACK_CONFIG, i.e. when the config
 *  could not be fetched at all — consumers must render an empty list as "no
 *  GE" (PlaceholderForm hides its GE controls).
 */
const fetchInstitutionConfig = async (): Promise<InstitutionConfig> => {
  const body = await apiClient.get<InstitutionConfigApiResponse>(
    '/api/institution/current',
  );
  const inst = body?.institution;
  if (!inst) {
    throw new Error('institution config response missing institution field');
  }
  const apiAreas = Array.isArray(inst.gen_ed_areas) ? inst.gen_ed_areas : [];
  return {
    slug: inst.slug || DEFAULT_SLUG,
    hasGradeData: Boolean(inst.has_grade_data),
    genEdAreas: apiAreas,
    supportsApCredits: Boolean(inst.supports_ap_credits),
    priorLearningCap:
      inst.prior_learning_cap === null
        ? null // explicit "no cap" (UC Merced) — distinct from a missing field
        : typeof inst.prior_learning_cap === 'number' && inst.prior_learning_cap > 0
          ? inst.prior_learning_cap
          : 60,
    // W43 P3 (#275): the institution's recordable marks drive the grade
    // dropdown. A cached pre-P3 config lacks the field → UMD list.
    gradeMarks:
      Array.isArray(inst.grade_marks) && inst.grade_marks.length > 0
        ? inst.grade_marks
        : [...UMD_GRADE_MARKS],
    graduationCredits:
      typeof inst.graduation_credits === 'number' && inst.graduation_credits > 0
        ? inst.graduation_credits
        : 120,
    workloadThresholds: parseWorkloadThresholds(inst.workload_thresholds),
    creditUnit: parseCreditUnit(inst.credit_unit),
    // An absent or empty list stays empty — no UMD substitute, unlike
    // gen_ed_areas: a department list is only meaningful for its own school.
    departments: Array.isArray(inst.departments)
      ? inst.departments.filter(
          (d): d is InstitutionDepartment => typeof d?.code === 'string' && d.code !== '',
        )
      : [],
    features: parseFeatures(inst.features),
  };
};

// =============================================================================
// THEME APPLICATION
// =============================================================================

/** Write the active institution's brand CSS variables to :root.
 *
 *  Tailwind classes (`bg-primary`, `text-accent`, …) resolve at runtime via
 *  `rgb(var(--color-primary) / <alpha-value>)`, so flipping the variables
 *  switches the entire brand palette without re-rendering or re-mounting.
 *
 *  Compile-time defaults live in index.css so the UI renders correctly
 *  before this side-effect runs (e.g. during first paint or in unit tests
 *  that mount without the provider).
 */
const applyTheme = (slug: string): void => {
  const theme = resolveTheme(slug);
  const root = document.documentElement;
  for (const [name, value] of Object.entries(theme.colors)) {
    root.style.setProperty(name, value);
  }
};

// =============================================================================
// PROVIDER
// =============================================================================

interface InstitutionConfigProviderProps {
  children: ReactNode;
}

export const InstitutionConfigProvider = ({
  children,
}: InstitutionConfigProviderProps) => {
  const { user } = useAuth();
  // Phase E3: derive the cache lookup slug from the active membership so a
  // multi-membership user's cache is keyed by their *selected* school.
  // Pre-auth / first-visit: DEFAULT (subdomain host fallback removed with
  // the 2026-07-17 subdomain teardown).
  const { activeSlug, loading: membershipsLoading } = useActiveInstitution();
  const resolvedSlug = activeSlug ?? DEFAULT_SLUG;

  // Initialize from cache immediately (or fallback if cache is stale/empty)
  // so components rendering at first paint don't see a flash of "wrong" gating.
  const [config, setConfig] = useState<InstitutionConfig>(
    () => loadFromCache(resolvedSlug) ?? FALLBACK_CONFIG,
  );
  // `loading` is derived during render, not set from an effect: the render
  // in which a user appears (or the active school changes) must already
  // read true, or a gate sees one frame of the fallback config and acts on
  // it. `resolvedKey` is the user+school whose fetch last settled. While
  // memberships load the school is not known yet, so a fetch that settles
  // first ("no school") must not read as loaded: it would flip back to
  // true when the school arrives. A user with no memberships settles once
  // they have loaded.
  const configKey = user ? `${user.id}:${activeSlug ?? ''}` : null;
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  const loading = configKey !== null && (membershipsLoading || resolvedKey !== configKey);

  // Only the latest request may apply. On a hard refresh the first fetch
  // starts before memberships resolve and a second follows once they do;
  // after a school switch the earlier one is for the old school.
  const latestRequest = useRef(0);

  const refresh = useCallback(async (key: string, slug: string | null) => {
    const request = ++latestRequest.current;
    try {
      // apiClient resolves the access token via TokenManager, so we don't
      // need to thread session through here.
      const fresh = await fetchInstitutionConfig();
      if (request !== latestRequest.current) return;
      setConfig(fresh);
      saveToCache(fresh);
    } catch (error) {
      // Keep what we have if it is this school's (cache or an earlier
      // fetch); after a school switch it is the old school's, so use the
      // new school's cache or the fallback, as a fresh load would. With no
      // known school (memberships failed or not loaded) keep it too: the
      // backend still answered for the user's saved school, and there is
      // nothing to compare it against. No throw — institution config is
      // non-critical and the rest of the app should not break because this
      // fetch failed.
      if (request === latestRequest.current && slug !== null) {
        setConfig((current) =>
          current.slug === slug ? current : loadFromCache(slug) ?? FALLBACK_CONFIG,
        );
      }
      // eslint-disable-next-line no-console
      console.warn('InstitutionConfig fetch failed; keeping cached/fallback', error);
    } finally {
      if (request === latestRequest.current) setResolvedKey(key);
    }
  }, []);

  useEffect(() => {
    if (!configKey) {
      // Pre-auth or signed out: stay on cache/fallback. Don't fetch, drop
      // any fetch still in flight for the previous user, and forget what
      // was loaded, so signing back in reads as loading until this
      // session's fetch settles.
      latestRequest.current += 1;
      setResolvedKey(null);
      return;
    }
    refresh(configKey, activeSlug);
    // Re-fetch when the active membership slug changes (user switched
    // schools) so the per-institution config (data sources, GE areas)
    // matches the newly-active school. `user` is listed too: AuthContext
    // hands over a new object on every auth event (token refresh roughly
    // hourly), which is how an open tab picks up a config change. Same
    // user and school, so `loading` stays false during that refetch.
  }, [user, configKey, activeSlug, refresh]);

  // Apply brand CSS variables whenever the active slug changes. Runs on
  // mount (so the cache/fallback slug seeds the theme before paint) and
  // again after `refresh()` swaps in a fresh slug.
  useEffect(() => {
    applyTheme(config.slug);
  }, [config.slug]);

  const value = useMemo<InstitutionConfigContextValue>(
    () => ({
      config,
      loading,
      isFeatureEnabled: (slug) => featureEnabled(config.features, slug),
    }),
    [config, loading],
  );

  return (
    <InstitutionConfigContext.Provider value={value}>
      {children}
    </InstitutionConfigContext.Provider>
  );
};
